import { Chip } from "@/components/Chip";
import { DataTable, type DataCol } from "@/components/DataTable";
import { PageHead } from "@/components/PageHead";
import { useProductPicker } from "@/modules/products/ProductPicker";
import { useSpaces, type SpaceDefinition } from "@/modules/products/fixtures";
import { SoonButton } from "@/components/SoonButton";

const COLS: DataCol<SpaceDefinition>[] = [
  { key: "name", label: "Space", width: 190, render: (s) => s.name },
  { key: "slug", label: "Slug", width: 160, mono: true, render: (s) => s.slug },
  {
    key: "scope", label: "Scope", width: 200,
    render: (s) => s.private_to
      ? <Chip tone="warn">private · {s.private_to}</Chip>
      : <Chip tone="mute">global default</Chip>,
  },
  { key: "alloc", label: "Allocated to", width: 120, render: (s) => `${s.allocations} companies` },
  {
    key: "act", label: "", width: 250,
    render: () => (
      <span style={{ display: "flex", gap: 6 }}>
        <SoonButton className="btn ghost sm" why="edits the permission defaults for this space">Permissions</SoonButton>
        <SoonButton className="btn ghost sm" why="renames a space">Edit</SoonButton>
        <SoonButton className="btn ghost sm" why="removes a space that has no allocations">Delete</SoonButton>
      </span>
    ),
  },
];

/** Spaces — a product's partitions.
 *
 *  STAGE A: ported as Fabric has them. Spaces are on the stage-C list; nothing
 *  here anticipates that, because porting and redesigning at once means a
 *  failure has two possible causes.
 */
export function SpacesPage() {
  const { product, picker } = useProductPicker();
  const spaces = useSpaces(product.product_id);

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Products · Spaces"
        title="Spaces"
        desc="Each space carries its own permission defaults and its own file library. A global space may be allocated to any company; a private one belongs to exactly one."
        right={picker}
      />

      <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
        <SoonButton className="btn teal sm" why="creates a space under this product">Add space</SoonButton>
      </div>

      <DataTable rows={spaces} cols={COLS} rowKey={(s) => s.space_id}
        empty={<div className="panel panel-pad cap">No spaces defined for this product.</div>} />
    </div>
  );
}
